import { useEffect, useState } from 'react';
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { admin_api, auth } from '@/lib/api';
import {
  ChartLineUp, Envelope, Calendar, CurrencyDollar, UserPlus, ShieldCheck,
  SignOut, PencilSimple, Star, House, Van, IdentificationBadge, CalendarBlank
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: ChartLineUp, end: true },
  { to: '/admin/enquiries', label: 'Enquiries', icon: Envelope },
  { to: '/admin/bookings', label: 'Bookings', icon: Calendar },
  { to: '/admin/quotes', label: 'Quotes', icon: CurrencyDollar },
  { to: '/admin/referrals', label: 'Referrals', icon: UserPlus },
  { to: '/admin/schedule', label: 'Schedule', icon: CalendarBlank },
  { to: '/admin/fleet', label: 'Fleet', icon: Van },
  { to: '/admin/drivers', label: 'Drivers', icon: IdentificationBadge },
  { to: '/admin/testimonials', label: 'Testimonials', icon: Star },
  { to: '/admin/cms', label: 'CMS', icon: PencilSimple },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('swift_admin_token');
    if (!token) { nav('/admin/login'); return; }
    auth.me().then(d => setEmail(d.email)).catch(() => { nav('/admin/login'); });
  }, [nav]);

  const logout = () => {
    localStorage.removeItem('swift_admin_token');
    localStorage.removeItem('swift_admin_email');
    toast.success('Signed out.');
    nav('/admin/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-layout">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:flex flex-col justify-between border-r border-neutral-200 bg-white p-6 sticky top-0 h-screen">
          <div>
            <Link to="/admin" className="flex items-center gap-2.5 mb-10">
              <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center">
                <ShieldCheck size={20} weight="fill" className="text-white" />
              </div>
              <div className="leading-none">
                <div className="font-display font-extrabold text-ink text-lg">Inclusive Mobility Admin</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-ink-muted font-semibold">Operations</div>
              </div>
            </Link>
            <nav className="space-y-1">
              {NAV.map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`admin-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                      isActive ? 'bg-brand text-white' : 'text-ink-soft hover:text-ink hover:bg-neutral-100'
                    }`
                  }
                >
                  <n.icon size={18} weight="duotone" /> {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div>
            <Link to="/" className="flex items-center gap-2 text-xs text-ink-muted hover:text-brand mb-4">
              <House size={14} /> Back to public site
            </Link>
            <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200">
              <div className="text-xs text-ink-muted">Signed in as</div>
              <div className="text-sm font-medium text-ink truncate">{email || '—'}</div>
              <button onClick={logout} data-testid="admin-logout" className="mt-3 w-full flex items-center justify-center gap-2 text-xs font-semibold text-ink-soft hover:text-brand transition">
                <SignOut size={14} /> Sign out
              </button>
            </div>
          </div>
        </aside>

        {/* mobile top nav */}
        <div className="lg:hidden bg-white border-b border-neutral-200 p-4 flex items-center justify-between sticky top-0 z-30">
          <Link to="/admin" className="font-display font-bold text-ink">Inclusive Mobility Admin</Link>
          <button onClick={logout} className="text-xs text-ink-muted">Sign out</button>
        </div>

        <main className="p-6 md:p-10 min-h-screen">
          <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto no-scrollbar">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `whitespace-nowrap px-3 py-2 rounded-lg text-xs font-semibold ${isActive ? 'bg-brand text-white' : 'bg-white text-ink-soft border border-neutral-200'}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
